import React, { useState, useRef, useEffect, CSSProperties } from 'react'
import {
  BaseSelectPickerWrapper,
  PickerToggle,
  PickerChevron,
  PickerMenu,
  MenuItem,
  FormDateRangePickerStyled,
  FormDatePickerStyled,
  ToggleText,
} from './styles/StandardSelectPicker.styles'
import theme from '../../styles/theme'

import CustomIcons from './CustomIcons'

// TypeScript interfaces
interface SelectPickerItem {
  value: string | number | null
  label: string
}

interface MultiSelectPickerItem {
  value: string | number
  label: string
}

interface SelectPickerProps {
  id?: string
  data?: SelectPickerItem[]
  defaultValue?: string | number | null
  value?: string | number | null
  onChange?: (value: string | number | null) => void
  placeholder?: string
  className?: string
  disabledItemValues?: (string | number | null)[]
  disabled?: boolean
  style?: CSSProperties
  interceptClickWaitOnConfirm?: (
    value: string | number | null,
    label: string,
    selectedValue: string | number | null,
    data: SelectPickerItem[],
  ) => Promise<boolean>
  'data-testid'?: string
}

interface ThickSelectPickerProps extends Omit<SelectPickerProps, 'className'> {}

interface FormSelectPickerProps extends Omit<SelectPickerProps, 'className'> {}

interface MultiSelectPickerProps {
  id?: string
  data?: MultiSelectPickerItem[]
  value?: (string | number)[]
  onChange?: (value: (string | number)[]) => void
  placeholder?: string
  disabled?: boolean
  style?: CSSProperties
  'data-testid'?: string
}

interface FormMultiSelectPickerProps
  extends Omit<MultiSelectPickerProps, 'className'> {}

interface FormDateRangePickerProps {
  [key: string]: any // For rsuite DateRangePicker props
}

interface FormDatePickerProps {
  [key: string]: any // For rsuite DatePicker props
}

// Base SelectPicker component
const SelectPicker: React.FC<SelectPickerProps> = ({
  id,
  data = [],
  defaultValue = '',
  value,
  onChange,
  placeholder = 'Select',
  className,
  disabledItemValues = [],
  disabled = false,
  style,
  interceptClickWaitOnConfirm,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  // Use value prop if provided, otherwise use defaultValue
  const [selectedValue, setSelectedValue] = useState<string | number | null>(
    value || defaultValue,
  )
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Validate disabledItemValues
  useEffect(() => {
    const invalidDisabledValues = disabledItemValues.filter(
      (value) => !data.some((item) => item.value === value),
    )

    if (invalidDisabledValues.length > 0) {
      console.warn(
        `Warning: The following disabled values do not exist in the data array: ${invalidDisabledValues.join(', ')}`,
      )
    }
  }, [data, disabledItemValues])

  // Add effect to update selected value when value prop changes
  useEffect(() => {
    if (value !== undefined) {
      setSelectedValue(value)
    }
  }, [value])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = async (value: string | number | null, label: string) => {
    if (disabled || disabledItemValues.includes(value)) {
      return
    }

    // Check if interception callback is provided
    if (interceptClickWaitOnConfirm) {
      try {
        const shouldConfirm = await interceptClickWaitOnConfirm(
          value,
          label,
          selectedValue,
          data,
        )
        if (shouldConfirm) {
          select(value, label)
        }
      } catch (error) {
        console.error('Error in interceptClickWaitOnConfirm:', error)
        // Optionally proceed with selection or handle error as needed
      }
    } else {
      // Normal flow - proceed with selection
      select(value, label)
    }
  }

  const select = (value: string | number | null, label: string) => {
    setSelectedValue(value)
    setIsOpen(false)
    if (onChange) onChange(value)
  }

  const displayValue =
    data.find((item) => item.value === selectedValue)?.label || placeholder

  return (
    <BaseSelectPickerWrapper
      ref={wrapperRef}
      className={`${className || ''} ${disabled ? 'disabled' : ''}`}
      style={style}
      data-testid={dataTestId}
    >
      <PickerToggle
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={disabled ? 'disabled' : ''}
        id={id}
        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
      >
        <ToggleText title={displayValue}>{displayValue}</ToggleText>
        <PickerChevron>
          <CustomIcons.ChevronDownIcon
            disabled={disabled}
            size="xxs"
            color={
              disabled ? theme.colors.fadedText : theme.colors.veryVeryFadedText
            }
          />
        </PickerChevron>
      </PickerToggle>
      <div
        style={{
          width: '100%',
          height: 0,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
        }}
      >
        {isOpen && (
          <PickerMenu
            data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
          >
            {data.map((item) => (
              <MenuItem
                key={item.value}
                onClick={() => handleSelect(item.value, item.label)}
                className={`
                                ${selectedValue === item.value ? 'selected' : ''}
                                ${disabledItemValues.includes(item.value) ? 'disabled' : ''}
                            `}
                data-testid={
                  dataTestId ? `${dataTestId}-option-${item.value}` : undefined
                }
              >
                {item.label}
              </MenuItem>
            ))}
          </PickerMenu>
        )}
      </div>
    </BaseSelectPickerWrapper>
  )
}

// Updated Thick and Form variants using the custom SelectPicker
const ThickSelectPicker: React.FC<ThickSelectPickerProps> = ({ ...props }) => {
  return <SelectPicker {...props} className="thick-select-picker" />
}

const FormSelectPicker: React.FC<FormSelectPickerProps> = ({ ...props }) => {
  return <SelectPicker {...props} className="form-select-picker" />
}

// Multi-select picker with checkboxes
const MultiSelectPicker: React.FC<MultiSelectPickerProps> = ({
  id,
  data = [],
  value = [],
  onChange,
  placeholder = 'Select',
  disabled = false,
  style,
  'data-testid': dataTestId,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleToggleItem = (itemValue: string | number) => {
    if (disabled) return

    const newValue = value.includes(itemValue)
      ? value.filter((v) => v !== itemValue)
      : [...value, itemValue]

    if (onChange) onChange(newValue)
  }

  const displayValue =
    value.length === 0
      ? placeholder
      : value.length === 1
        ? data.find((item) => item.value === value[0])?.label || placeholder
        : `${value.length} selected`

  return (
    <BaseSelectPickerWrapper
      ref={wrapperRef}
      className={`form-select-picker ${disabled ? 'disabled' : ''}`}
      style={style}
      data-testid={dataTestId}
    >
      <PickerToggle
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={disabled ? 'disabled' : ''}
        id={id}
        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
      >
        <ToggleText title={displayValue}>{displayValue}</ToggleText>
        <PickerChevron>
          <CustomIcons.ChevronDownIcon
            disabled={disabled}
            size="xxs"
            color={
              disabled ? theme.colors.fadedText : theme.colors.veryVeryFadedText
            }
          />
        </PickerChevron>
      </PickerToggle>
      <div
        style={{
          width: '100%',
          height: 0,
          position: 'relative',
          zIndex: 1,
          display: 'flex',
        }}
      >
        {isOpen && (
          <PickerMenu
            data-testid={dataTestId ? `${dataTestId}-menu` : undefined}
          >
            {data.map((item) => (
              <MenuItem
                key={item.value}
                onClick={(e) => {
                  e.stopPropagation()
                  handleToggleItem(item.value)
                }}
                className={value.includes(item.value) ? 'selected' : ''}
                data-testid={
                  dataTestId ? `${dataTestId}-option-${item.value}` : undefined
                }
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                <input
                  type="checkbox"
                  checked={value.includes(item.value)}
                  onChange={() => {}}
                  style={{ pointerEvents: 'none' }}
                />
                {item.label}
              </MenuItem>
            ))}
          </PickerMenu>
        )}
      </div>
    </BaseSelectPickerWrapper>
  )
}

const FormMultiSelectPicker: React.FC<FormMultiSelectPickerProps> = ({
  ...props
}) => {
  return <MultiSelectPicker {...props} />
}

const FormDateRangePicker: React.FC<FormDateRangePickerProps> = ({
  ...props
}) => {
  return <FormDateRangePickerStyled {...props} cleanable={false} />
}

const FormDatePicker: React.FC<FormDatePickerProps> = ({ ...props }) => {
  return <FormDatePickerStyled {...props} cleanable={false} isoWeek />
}

export {
  ThickSelectPicker,
  FormSelectPicker,
  FormMultiSelectPicker,
  FormDateRangePicker,
  FormDatePicker,
  SelectPicker,
  MultiSelectPicker,
}
